import { userApi } from "@/api";
import LoggingAPI from "@/api/logging";
import { FormTitle } from "@/components/Forms/components/MonolithFormComponents";
import ObserverSelector from "@/components/Forms/components/Selectors/ObserverSelector";
import RoleSelector from "@/components/Forms/components/Selectors/RoleSelector";
import { usePermissions } from "@/hooks/usePermissions";
import { User } from "@/types/Users";
import { useForm } from "@mantine/form";
import { Grid, Button } from "@monolith-forensics/monolith-ui";
import { useSnackbar, VariantType } from "notistack";
import { FC } from "react";

interface UserRolesBlockProps {
  userData: User;
  readOnly?: boolean;
  onSubmit: () => void;
}

const UserRolesBlock: FC<UserRolesBlockProps> = ({ userData, onSubmit }) => {
  const form = useForm({
    mode: "controlled",
    initialValues: {
      role_id: userData?.role_id,
      observer: userData?.observer,
    },
  });

  const { currentUser, MONOLITH_PERMISSIONS, hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    const formData = form.getValues();

    // If no values were updated, don't change anything
    if (
      formData.role_id === userData.role_id &&
      formData.observer === userData.observer
    ) {
      onSubmit();
      return;
    }

    try {
      await userApi.updateUser(userData.user_id, {
        role_id: formData.role_id,
        observer: formData.observer,
      });

      enqueueSnackbar(`User permissions update successfully`, {
        variant: "success" as VariantType,
      });
      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `User ${userData.username} permissions updated`,
        event: "user:update",
        object_type: "user",
      });

      form.resetTouched();

      onSubmit();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`An error occurred while updating user`, {
        variant: "error" as VariantType,
      });
    }
  };

  return (
    <div
      style={{
        margin: "35px 0",
      }}
    >
      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <FormTitle>Role & Observer Status</FormTitle>
      </div>
      <Grid col={2}>
        <RoleSelector
          key={form.key("role_id")}
          disabled={!hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE)}
          {...form.getInputProps("role_id")}
        />
        <ObserverSelector
          key={form.key("observer")}
          disabled={!hasPermission(MONOLITH_PERMISSIONS.USERS_MANAGE)}
          {...form.getInputProps("observer")}
        />
      </Grid>
      {form?.isTouched() && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <Button
            size="xs"
            color="primary"
            variant="light"
            onClick={handleSubmit}
          >
            Save Role Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserRolesBlock;
